import { useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Collapse, TextField, Typography,
} from '@mui/material'
import { Eyebrow, Surface, getConfig, tokens, useAuthProviders } from '@agribot/polygon-checks-ui'
import { useSignInWithToken } from './token'
import { PersonSignIn } from './edition'

/**
 * Sign in.
 *
 * Two ways in, and the screen offers both. Auth0 is for people and is the
 * only thing that opens a session. An access token is the other: a root key
 * provisions a user and returns one, and an integration sends it on every
 * request — so somebody holding one should be able to drive this
 * application as that user, rather than only through their own code.
 *
 * The token is deliberately the secondary option, folded away and labelled
 * for what it is. It is the credential of an integration, not of a person,
 * and putting it beside the button as an equal choice would suggest
 * otherwise.
 *
 * Ours alone: a partner's application signs its people in its own way and
 * hands the component their token. Auth0 here is our tenant.
 */
export function SignIn() {
  const { data: providers, isLoading } = useAuthProviders()
  const withToken = useSignInWithToken()
  const [showToken, setShowToken] = useState(false)
  const [token, setToken] = useState('')

  // The tenant refused, or the person declined. The callback puts its reason
  // here rather than rendering an error page of its own.
  const returned = new URLSearchParams(window.location.search).get('error')



  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!token.trim()) return
    withToken.mutate(token)
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 6 }}>
      <Surface sx={{ width: 420, p: 8 }}>
        <Box
          component="img"
          src="/logo-mark.svg"
          alt="agribot"
          sx={{ width: 36, height: 36, display: 'block', mb: 5 }}
        />
        <Eyebrow sx={{ mb: 2 }}>agribot · polygon checks</Eyebrow>
        <Typography variant="h2" sx={{ mb: 2 }}>Sign in</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 6 }}>
          Validate supplier plot polygons before a due-diligence submission.
        </Typography>

        {returned && <Alert severity="error" sx={{ mb: 5 }}>{returned}</Alert>}
        {withToken.isError && (
          <Alert severity="error" sx={{ mb: 5 }}>
            {/* The server's own words: it distinguishes a revoked token from
                an unknown one, and that distinction is worth passing on. */}
            {withToken.error instanceof TypeError || (withToken.error as Error)?.name === 'TimeoutError'
              // No response at all: a wrong address, CORS, or nothing answering.
              ? `Could not reach ${getConfig().baseUrl}. Check the address, and that the API allows requests from ${window.location.origin}.`
              : (withToken.error as Error)?.message
                || 'That token was not accepted. It may have been revoked, or belong to another deployment.'}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={18} />
          </Box>
        )}

        <PersonSignIn providers={providers} />

        {/* --- The other way in --- */}
        <Box sx={{ mt: providers?.auth0 ? 5 : 4 }}>
          <Button
            variant="text"
            onClick={() => setShowToken((v) => !v)}
            sx={{ color: 'text.secondary', px: 0 }}
          >
            {showToken ? 'Hide' : 'Use a token or root key instead'}
          </Button>

          <Collapse in={showToken}>
            <Box component="form" onSubmit={submit} sx={{ pt: 2 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Either a user token (<code>eyJ…</code>), which shows the
                application as that user, or a partner root key
                (<code>pck_…</code>), which manages that organisation’s users
                and reads its usage.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="eyJhbGciOi…"
                inputProps={{ 'aria-label': 'Access token', spellCheck: false }}
                sx={{ mb: 3, '& textarea': { font: `12px ${tokens.font.mono}` } }}
              />
              <Button
                type="submit"
                fullWidth
                variant="outlined"
                disabled={!token.trim() || withToken.isPending}
              >
                {withToken.isPending ? 'Checking…' : 'Continue with token'}
              </Button>
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', mt: 2, display: 'block' }}
              >
                Kept in this browser’s local storage, which a script on the page
                can read — unlike the Auth0 session. Revoke it with{' '}
                <code>POST /users/&#123;id&#125;/revoke</code> if it gets out.
              </Typography>
            </Box>
          </Collapse>
        </Box>
      </Surface>
    </Box>
  )
}
